<script lang="ts" setup>
import { BOTTOM_NAVIGATION_ITEMS } from '~/resources'
import type { BottomNavigationItems } from '~/types/'
import { contactUs } from '~/resources/contact-us'
import { SportPlatform } from '~/enums'
import { useGameToastStore } from '#lib/stores'
import { useHiddenToScroll } from '~/composables/use-hidden-to-scroll'
import { useCustomLiveChat } from '~/composables/use-custom-live-chat'

const route = useRoute()
const navigationItems = reactive<BottomNavigationItems[]>(
  BOTTOM_NAVIGATION_ITEMS,
)
const { openAuthModal } = useAuth()
const { openLiveChat } = useCustomLiveChat()
const { isLogged } = useUser()
const isOpenModalContacts = ref(false)
const {
  TELEGRAM_CHAT_BOT,
  TELEGRAM_LINK,
  YOUTUBE_LINK,
  FANPAGE_LINK,
  BRAND_NAME,
} = useRuntimeConfig().public
const ELEMENT_CENTER_INDEX = 2
const modalSupportContacts = ref()
const modalSupportSabaPlatform = ref()
const { isIos } = useDevice()
const gameToastStore = useGameToastStore()
const { isOpenModeGameVersion } = storeToRefs(gameToastStore)
const { navigateWithURL, lastURL } = useNavigate()
const { isVisible } = useHiddenToScroll({
  defaultValue: true,
})

const supportTimeout = ref()
const supportSabaTimeout = ref()

onBeforeUnmount(() => {
  if (supportTimeout.value) {
    clearTimeout(supportTimeout.value)
  }
  if (supportSabaTimeout.value) {
    clearTimeout(supportSabaTimeout.value)
  }
})

const handleItemMenuClick = (item: BottomNavigationItems): void => {
  if (item.loginRequired && !isLogged.value) {
    openAuthModal()
    return
  }
  const currentActiveStatus = item.isActive
  resetActiveBarItemStatus()
  if (item.modalIdOpen) {
    item.isActive = !currentActiveStatus
    isOpenModalContacts.value = !isOpenModalContacts.value
    return
  }
  item.isActive = true
  navigateTo(item.link)
}

const resetActiveBarItemStatus = (): void => {
  navigationItems.forEach((item) => {
    item.isActive = false
  })
}

const activeBarItemStatus = (path: string): void => {
  navigationItems.forEach((item) => {
    if (item.routeActive) {
      item.isActive = item.routeActive.some(
        (routeActive) => routeActive === path,
      )
    }
  })
}

onClickOutside(modalSupportContacts, () => {
  if (supportTimeout.value) {
    clearTimeout(supportTimeout.value)
  }
  supportTimeout.value = setTimeout(() => {
    isOpenModalContacts.value = false
    resetActiveBarItemStatus()
    activeBarItemStatus(route.path)
  })
})

onClickOutside(modalSupportSabaPlatform, () => {
  if (supportSabaTimeout.value) {
    clearTimeout(supportSabaTimeout.value)
  }
  supportSabaTimeout.value = setTimeout(() => {
    gameToastStore.setOpenModeGameVersion(false)
  })
})

const onClickSportPlatform = (platform: SportPlatform) => {
  gameToastStore.setOpenModeGameVersion(false)
  navigateWithURL(
    lastURL?.value as string,
    true,
    platform === SportPlatform.Mobile,
  )
}

watch(
  () => route.path,
  (value) => {
    activeBarItemStatus(value)
  },
  { immediate: true },
)
</script>

<template>
  <div
    :class="[
      'bottom-navigation-bar',
      { 'is-ios': isIos, 'bottom-navigation-bar--hidden': !isVisible },
    ]"
  >
    <div class="bottom-navigation-bar__menu">
      <a
        v-for="(item, index) in navigationItems"
        :key="index"
        :class="[
          'bottom-navigation-bar__item',
          { 'bottom-navigation-bar__item--active': item.isActive },
          {
            'bottom-navigation-bar__item--center':
              index === ELEMENT_CENTER_INDEX,
          },
        ]"
        :href="item.link"
        @click.prevent="handleItemMenuClick(item)"
      >
        <div v-if="index === ELEMENT_CENTER_INDEX" class="curve-center" />

        <img
          v-if="index !== ELEMENT_CENTER_INDEX && item.isActive"
          class="border-top-active"
          src="/images/bottom-navigation-mobile/bar-top-active.webp"
          alt="top bar"
        />

        <img
          class="bottom-navigation-bar__item__icon"
          :src="
            item.isActive && index !== ELEMENT_CENTER_INDEX
              ? item.iconActive
              : item.icon
          "
          :alt="`Menu ${item.title}`"
        />
        {{ item.title }}
      </a>
    </div>

    <div
      v-if="isOpenModalContacts"
      ref="modalSupportContacts"
      class="modal-support-contacts-mobile"
    >
      <div class="text-support">Kênh hỗ trợ</div>
      <div class="support-contacts">
        <NuxtLink
          :to="YOUTUBE_LINK"
          target="_blank"
          class="support-contacts__item"
        >
          <div class="support-contacts__item__icon">
            <img src="/icons/icon-youtube.png" alt="Youtube" />
          </div>
          <div class="support-contacts__item__title">Youtube</div>
        </NuxtLink>
        <div class="support-contacts__item" @click="openLiveChat()">
          <div class="support-contacts__item__icon">
            <img src="/icons/icon-live-chat.png" :alt="contactUs.title" />
          </div>
          <div class="support-contacts__item__title">
            {{ contactUs.title }}
          </div>
        </div>
        <NuxtLink
          :to="TELEGRAM_CHAT_BOT"
          target="_blank"
          class="support-contacts__item"
        >
          <div class="support-contacts__item__icon">
            <img src="/icons/icon-telegram.png" alt="Telegram" />
          </div>
          <div class="support-contacts__item__title">CSKH</div>
        </NuxtLink>
        <NuxtLink
          :to="TELEGRAM_LINK"
          target="_blank"
          class="support-contacts__item"
        >
          <div class="support-contacts__item__icon">
            <img src="/icons/icon-telegram.png" alt="Telegram channel" />
          </div>
          <div class="support-contacts__item__title">Kênh {{ BRAND_NAME }}</div>
        </NuxtLink>
        <NuxtLink
          :to="FANPAGE_LINK"
          target="_blank"
          class="support-contacts__item"
        >
          <div class="support-contacts__item__icon">
            <img src="/icons/icon-facebook.png" alt="Fanpage" />
          </div>
          <div class="support-contacts__item__title">Fanpage</div>
        </NuxtLink>
      </div>
    </div>

    <div
      v-if="isOpenModeGameVersion"
      ref="modalSupportSabaPlatform"
      class="modal-support-saba-platform-mobile"
    >
      <div class="saba-platform">
        <div class="saba-platform__header">
          <div class="saba-platform__header-title">Vui lòng chọn phiên bản</div>
          <div
            class="icon-close-popup"
            @click="isOpenModeGameVersion = false"
          ></div>
        </div>
        <div class="saba-platform__body">
          <div
            class="saba-platform__item"
            @click="onClickSportPlatform(SportPlatform.Desktop)"
          >
            <div class="saba-platform__item-icon">
              <img src="/icons/icon-desktop.svg" alt="Desktop" />
            </div>
            <div class="saba-platform__item-title">Bản Desktop</div>
          </div>
          <div
            class="saba-platform__item"
            @click="onClickSportPlatform(SportPlatform.Mobile)"
          >
            <div class="saba-platform__item-icon">
              <img src="/icons/icon-mobile.svg" alt="Mobile" />
            </div>
            <div class="saba-platform__item-title">Bản Mobile</div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="isOpenModalContacts || isOpenModeGameVersion"
    class="overlay-modal-contact"
  />
</template>

<style lang="scss" scoped>
@import '~/assets/styles/components/mobile/common/bottom-navigation-bar.scss';
</style>
